/**
 * Pied de page du premier ecran : langue, reseaux, contenu du site.
 *
 * C'EST LE MAILLAGE DU SITE. Un robot d'indexation ne suit que des `href` :
 * sans ce pied de page, les pages de regles, de modes et le blog n'auraient
 * aucun lien entrant et seraient invisibles pour Google, meme declarees au
 * sitemap. D'ou `SiteLink`, un vrai `<a>`, et non les `<button>` d'avant.
 *
 * Le selecteur de langue est ici et pas dans un menu : c'est la seule page ou
 * un nouveau venu peut la changer avant de comprendre le reste.
 *
 * DEUX RANGEES, et les liens legaux n'y sont plus : ils vivent dans
 * `LegalFooter`, tout en bas de la page. Le compte de rangees est le meme
 * qu'avant (les reseaux ont pris la place des liens legaux), donc la hauteur
 * du bloc ne bouge pas et le premier ecran non plus. C'est la contrainte du
 * CLAUDE.md : les boutons de l'accueil ne se decalent pas d'un pixel.
 *
 * Les libelles passent par `t()`, comme tout le reste. Ils ont ete ecrits en
 * dur en francais un temps : le pied de page portait « Comment jouer » sous un
 * selecteur affichant « Portugues », a cote de liens legaux traduits, et le
 * lien menait pourtant bien a la page portugaise.
 */
import type { ReactElement } from 'react';
import { useT } from '../locales/useT.js';
import { contentPrefix } from '../content/index.js';
import { useUiStore } from '../store/uiStore.js';
import { usePublicConfig } from '../publicConfig.js';
import { LanguagePicker } from './LanguagePicker.js';
import { SiteLink } from './SiteLink.js';
import { DiscordIcon, InstagramIcon } from './icons.js';

/** Compte Instagram du jeu. Public et fixe : rien a configurer. */
const INSTAGRAM_URL = 'https://www.instagram.com/retusch.io/';

export function SiteFooter(): ReactElement {
  const t = useT();
  // Les liens de contenu suivent la langue affichee. Sans cela, un joueur en
  // anglais cliquait « How to play » et atterrissait sur la page francaise.
  const prefix = contentPrefix(useUiStore((state) => state.language));
  // Le lien Discord vient du SERVEUR : c'est lui qui detient la configuration.
  // Sans lien configure, le bouton n'existe pas plutot que de ne mener nulle
  // part, exactement comme le bouton de partage.
  const { discordInvite } = usePublicConfig();

  return (
    <footer className="site-footer">
      <div className="site-footer__row">
        <LanguagePicker />
        <div className="site-footer__social">
          {discordInvite !== null && (
            <a
              className="site-footer__icon"
              href={discordInvite}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={t('footer.discord')}
              title={t('footer.discord')}
            >
              <DiscordIcon />
            </a>
          )}
          <a
            className="site-footer__icon"
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={t('footer.instagram')}
            title={t('footer.instagram')}
          >
            <InstagramIcon />
          </a>
        </div>
      </div>

      <nav className="site-footer__group" aria-label="Retusch">
        <SiteLink to={`${prefix}/how-to-play`} className="site-footer__link">
          {t('footer.howToPlay')}
        </SiteLink>
        <SiteLink to={`${prefix}/modes`} className="site-footer__link">
          {t('footer.modes')}
        </SiteLink>
        <SiteLink to={`${prefix}/blog`} className="site-footer__link">
          {t('footer.blog')}
        </SiteLink>
      </nav>
    </footer>
  );
}

/**
 * Liens legaux, TOUT EN BAS de la page.
 *
 * Separes du pied de page principal parce qu'ils repondent a un autre besoin :
 * on ne les cherche pas en arrivant, on les cherche quand on les cherche. Les
 * mettre au bout du document est la convention du web, et cela libere une
 * rangee dans le premier ecran pour les liens qu'on clique vraiment.
 */
export function LegalFooter(): ReactElement {
  const t = useT();
  return (
    <footer className="legal-footer">
      <nav className="site-footer__group" aria-label={t('legal.legal')}>
        <SiteLink to="/legal" className="site-footer__link">
          {t('legal.legal')}
        </SiteLink>
        <SiteLink to="/privacy" className="site-footer__link">
          {t('legal.privacy')}
        </SiteLink>
        <SiteLink to="/terms" className="site-footer__link">
          {t('legal.terms')}
        </SiteLink>
      </nav>
    </footer>
  );
}
